import { convertUtcToLocal } from './dateUtils';

export const parseGPGGA = gpggaString => {
  if (!gpggaString || typeof gpggaString !== 'string') {
    return 'No GPGGA data';
  }

  try {
    const fields = gpggaString.split(',');

    if (fields.length < 14 || !fields[0].includes('GGA')) {
      return 'Invalid GPGGA format';
    }

    const utcTime = fields[1];
    const latitude = fields[2];
    const latDirection = fields[3];
    const longitude = fields[4];
    const lonDirection = fields[5];
    const fixQuality = fields[6];
    const satellites = fields[7];
    const hdop = fields[8];
    const altitude = fields[9];
    const altitudeUnits = fields[10];

    let formattedTime = 'Unknown';
    if (utcTime && utcTime.length >= 6) {
      const hours = utcTime.substring(0, 2);
      const minutes = utcTime.substring(2, 4);
      const seconds = utcTime.substring(4);

      const utcTimeStr = `${hours}:${minutes}:${seconds}`;

      const localTime = convertUtcToLocal(hours, minutes, seconds);

      formattedTime = `${utcTimeStr} UTC (${localTime})`;
    }

    let formattedLat = 'Unknown';
    if (latitude && latitude.length >= 4) {
      const latDegrees = parseFloat(latitude.substring(0, 2));
      const latMinutes = parseFloat(latitude.substring(2));
      const latDecimal = latDegrees + latMinutes / 60;
      formattedLat = `${latDecimal.toFixed(6)}° ${latDirection}`;
    }

    let formattedLon = 'Unknown';
    if (longitude && longitude.length >= 5) {
      const lonDegrees = parseFloat(longitude.substring(0, 3));
      const lonMinutes = parseFloat(longitude.substring(3));
      const lonDecimal = lonDegrees + lonMinutes / 60;
      formattedLon = `${lonDecimal.toFixed(6)}° ${lonDirection}`;
    }

    const fixQualityMap = {
      0: 'Invalid',
      1: 'GPS fix (SPS)',
      2: 'DGPS fix',
      3: 'PPS fix',
      4: 'RTK fixed',
      5: 'RTK float',
      6: 'Estimated (dead reckoning)',
      7: 'Manual input mode',
      8: 'Simulation mode',
    };

    const fixQualityDesc = fixQualityMap[fixQuality] || `Unknown (${fixQuality})`;
    const formattedAltitude = altitude ? `${altitude} ${altitudeUnits}` : 'Unknown';

    return `Time:${formattedTime}
Position: ${formattedLat}, ${formattedLon}
Fix Quality: ${fixQualityDesc}
Satellites: ${satellites || 'Unknown'}
HDOP: ${hdop || 'Unknown'}
Altitude: ${formattedAltitude}`;
  } catch (error) {
    console.error('Error parsing GPGGA:', error);
    return 'Error parsing GPGGA data';
  }
};

export const parseGNGST = gngstString => {
  if (!gngstString || typeof gngstString !== 'string') {
    return 'No GNGST data';
  }

  try {
    const fields = gngstString.split(',');

    if (fields.length < 9 || !fields[0].includes('GST')) {
      return 'Invalid GNGST format';
    }

    const utcTime = fields[1];
    const rms = fields[2];
    const semiMajorStd = fields[3];
    const semiMinorStd = fields[4];
    const orientation = fields[5];
    const latStd = fields[6];
    const lonStd = fields[7];
    const altStd = fields[8].split('*')[0];

    let formattedTime = 'Unknown';
    if (utcTime && utcTime.length >= 6) {
      const hours = utcTime.substring(0, 2);
      const minutes = utcTime.substring(2, 4);
      const seconds = utcTime.substring(4);

      const utcTimeStr = `${hours}:${minutes}:${seconds}`;

      const localTime = convertUtcToLocal(hours, minutes, seconds);

      formattedTime = `${utcTimeStr} UTC (${localTime})`;
    }

    const formatValue = (value, unit, description) => {
      if (!value || value === '' || isNaN(parseFloat(value))) {
        return `${description}: Unknown`;
      }
      const numValue = parseFloat(value);
      return `${description}: ${numValue.toFixed(3)} ${unit}`;
    };

    return `Time: ${formattedTime}
${formatValue(rms, 'm', 'RMS of Range Errors')}
${formatValue(semiMajorStd, 'm', 'Error Semi-Major Axis (1σ)')}
${formatValue(semiMinorStd, 'm', 'Error Semi-Minor Axis (1σ)')}
${formatValue(orientation, '°', 'Error Orientation')}
${formatValue(latStd, 'm', 'Latitude Std Dev (1σ)')}
${formatValue(lonStd, 'm', 'Longitude Std Dev (1σ)')}
${formatValue(altStd, 'm', 'Altitude Std Dev (1σ)')}`;
  } catch (error) {
    console.error('Error parsing GNGST:', error);
    return 'Error parsing GNGST data';
  }
};

// While the hub flashes its GNSS receiver it reports
// "Upgrading to <type> <version>" in the status `gnss_version` field.
const GNSS_UPGRADING_PREFIX = 'Upgrading to ';

// "<type> <version>" of the running GNSS firmware upgrade, '' when none.
export const getGnssUpgrading = status => {
  const version = typeof status?.gnss_version === 'string' ? status.gnss_version.trim() : '';
  return version.startsWith(GNSS_UPGRADING_PREFIX)
    ? version.slice(GNSS_UPGRADING_PREFIX.length).trim()
    : '';
};

// GNSS receiver vendor of a hub, as used in the gnss_fw_{type}_{version}
// upgrade command, derived from the status `gnss_version` prefix
// ("Septentrio-mosaic-H-4.15.1", "Unicore-UM982,R4.10Build17572,...").
// Returns '' when unknown.
export const getHubGnssType = status => {
  const version = typeof status?.gnss_version === 'string' ? status.gnss_version.trim() : '';
  if (/^septentrio-/i.test(version)) {
    return 'Septentrio';
  }
  if (/^unicore-/i.test(version)) {
    return 'Unicorecomm';
  }
  return '';
};
